// Enhanced Child Card Component with Dynamic Features
import React, { useState } from 'react';
import { Target, AlertCircle, X } from 'lucide-react';

const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

const DeleteChildModal = ({ child, onClose, onDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const totalSaved = parseFloat(child.total_saved || 0);
  const hasBalance = totalSaved > 0;

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/families/${child.family_id}/children/${child.id}`,
        { method: 'DELETE' }
      );

      const result = await response.json();

      if (result.success) {
        onDeleted(child.id);
      } else if (result.error === 'CHILD_HAS_BALANCE') {
        setError('balance');
      } else {
        setError('general');
      }
    } catch (err) {
      setError('general');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Remove Child</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error === 'balance' || hasBalance ? (
          // Has balance — can't delete
          <div>
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl mb-4">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 mb-1">
                  {child.first_name} has a balance of ${totalSaved.toFixed(2)}
                </p>
                <p className="text-sm text-amber-700">
                  Accounts with a remaining balance can't be removed online. Please reach out and we'll help you.
                </p>
              </div>
            </div>
            <a
              href="mailto:hello@growthsprouts.ai"
              className="block w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              Contact hello@growthsprouts.ai
            </a>
          </div>
        ) : error === 'general' ? (
          // Generic error
          <div>
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl mb-4">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">Something went wrong. Please try again.</p>
            </div>
            <button
              onClick={onClose}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          // Confirm delete
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove <span className="font-medium text-gray-900">{child.first_name}</span>'s account? This can't be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
                disabled={isDeleting}
              >
                {isDeleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EnhancedChildCard = ({ child, onChildPress, onAddMoney, onChildDeleted }) => {
  // Color themes for initials - softer, more subtle palette
  const colorThemes = [
    { bg: 'from-slate-400 to-slate-500', text: 'text-white' },
    { bg: 'from-blue-400 to-blue-500', text: 'text-white' },
    { bg: 'from-cyan-400 to-cyan-500', text: 'text-white' },
    { bg: 'from-emerald-400 to-emerald-500', text: 'text-white' },
    { bg: 'from-green-400 to-green-500', text: 'text-white' },
    { bg: 'from-amber-400 to-amber-500', text: 'text-white' },
    { bg: 'from-orange-400 to-orange-500', text: 'text-white' },
    { bg: 'from-rose-400 to-rose-500', text: 'text-white' },
    { bg: 'from-purple-400 to-purple-500', text: 'text-white' },
    { bg: 'from-indigo-400 to-indigo-500', text: 'text-white' },
  ];

  const totalSaved = parseFloat(child.total_saved || 0);
  const savingsGoal = parseFloat(child.savings_goal || 100);
  const progressPercent = Math.min((totalSaved / savingsGoal) * 100, 100);

  // Calculate total bonus earned - this should be calculated in your backend
  // when loading children data by summing bonus_earned from deposits table
  // WHERE child_id = child.id AND status = 'admin_approved'
  const totalBonusEarned = parseFloat(child.total_bonus || child.bonus_total || 0);

  // Get consistent color theme based on child's name
  const getColorTheme = (name) => {
    if (!name) return colorThemes[0];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colorThemes[hash % colorThemes.length];
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const colorTheme = getColorTheme(child.first_name);

  // Get first letter of name
  const initial = child.first_name ? child.first_name.charAt(0).toUpperCase() : 'U';

  // Dynamic level system with more personality
  const getLevel = (amount) => {
    if (amount >= 500) return {
      level: 'Money Master',
      badge: '🏆',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    };
    if (amount >= 200) return {
      level: 'Super Saver',
      badge: '⭐',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    };
    if (amount >= 100) return {
      level: 'Smart Saver',
      badge: '🌟',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    };
    if (amount >= 50) return {
      level: 'Growing Saver',
      badge: '✨',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    };
    if (amount >= 10) return {
      level: 'Learning Saver',
      badge: '🌱',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    };
    return {
      level: 'New Saver',
      badge: '🥚',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100'
    };
  };

  const levelInfo = getLevel(totalSaved);

  // Calculate streak and recent activity
  const getDaysActive = () => {
    if (child.last_deposit) {
      const lastDeposit = new Date(child.last_deposit);
      const daysSince = Math.floor((Date.now() - lastDeposit) / (1000 * 60 * 60 * 24));
      return daysSince;
    }
    return null;
  };

  const daysActive = getDaysActive();

  // Dynamic card border based on progress
  const getCardBorder = () => {
    if (progressPercent >= 100) return 'border-yellow-400 shadow-yellow-100';
    if (progressPercent >= 75) return 'border-purple-400 shadow-purple-100';
    if (progressPercent >= 50) return 'border-blue-400 shadow-blue-100';
    if (progressPercent >= 25) return 'border-green-400 shadow-green-100';
    return 'border-gray-200 shadow-gray-100';
  };

  // Recent activity indicator
  const getActivityStatus = () => {
    if (!daysActive) return null;
    if (daysActive <= 3) return { text: 'Active', color: 'text-green-600', dot: 'bg-green-500' };
    if (daysActive <= 7) return { text: 'Recent', color: 'text-yellow-600', dot: 'bg-yellow-500' };
    return { text: 'Quiet', color: 'text-gray-600', dot: 'bg-gray-400' };
  };

  const activityStatus = getActivityStatus();

  // Motivational messages based on progress
  const getMotivationalMessage = () => {
    if (progressPercent >= 100) return "Goal achieved! 🎉";
    if (progressPercent >= 90) return "So close to your goal!";
    if (progressPercent >= 75) return "Amazing progress!";
    if (progressPercent >= 50) return "Halfway there!";
    if (progressPercent >= 25) return "Great start!";
    if (totalSaved > 0) return "Keep it up!";
    return "Ready to start saving?";
  };

  return (
    <div className={`bg-white rounded-2xl p-5 flex flex-col items-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border-2 ${getCardBorder()} relative group overflow-hidden min-h-[420px]`}>
      {/* Background Pattern based on theme */}
      <div className={`absolute inset-0 opacity-5 bg-gradient-to-br ${colorTheme.bg}`} />

      {/* Activity Status Indicator */}
      {activityStatus && (
        <div className="absolute top-3 left-3 flex items-center space-x-1">
          <div className={`w-2 h-2 rounded-full ${activityStatus.dot} animate-pulse`} />
          <span className={`text-xs font-medium ${activityStatus.color}`}>
            {activityStatus.text}
          </span>
        </div>
      )}

      {/* Remove Button */}
      <button
        className="absolute top-3 right-3 text-gray-300 hover:text-red-400 transition-colors p-1"
        onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
        title="Remove child"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteChildModal
          child={child}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={(childId) => {
            setShowDeleteModal(false);
            if (onChildDeleted) onChildDeleted(childId);
          }}
        />
      )}

      {/* Achievement Confetti for goal achievers */}
      {progressPercent >= 100 && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-4 left-4 text-yellow-400 animate-bounce">✨</div>
          <div className="absolute top-6 right-6 text-pink-400 animate-pulse">🎉</div>
          <div className="absolute bottom-8 left-6 text-purple-400 animate-bounce delay-300">⭐</div>
        </div>
      )}

      {/* Initial Avatar with Dynamic Badge */}
      <div className={`w-[80px] h-[80px] rounded-2xl flex justify-center items-center mb-3 relative bg-gradient-to-br ${colorTheme.bg} shadow-lg`}>
        <span className={`text-3xl font-bold ${colorTheme.text} drop-shadow-sm`}>{initial}</span>
        {/* Level Badge */}
        <div className={`absolute -top-2 -right-2 w-[28px] h-[28px] ${levelInfo.bgColor} rounded-full flex justify-center items-center border-2 border-white text-xs shadow-md`}>
          <span>{levelInfo.badge}</span>
        </div>
      </div>

      {/* Level Status */}
      <div className={`text-xs font-semibold mb-1 px-2 py-1 rounded-full ${levelInfo.bgColor} ${levelInfo.color}`}>
        {levelInfo.level}
      </div>

      {/* Child Name with personality */}
      <h3 className="text-lg font-bold text-gray-900 mb-1 text-center">
        {child.first_name}
      </h3>

      {/* Motivational Message */}
      <div className="text-xs text-emerald-600 italic text-center mb-3 min-h-[16px]">
        {getMotivationalMessage()}
      </div>

      {/* Balance with Animation - Fixed Height Container */}
      <div className="text-center mb-2 min-h-[60px] flex flex-col justify-center">
        <div className={`text-2xl font-bold bg-gradient-to-r ${colorTheme.bg} bg-clip-text text-transparent`}>
          ${totalSaved.toFixed(2)}
        </div>

        {/* Bonus Summary - Fixed Height to Prevent Layout Shift */}
        <div className="min-h-[16px] mt-1">
          {totalBonusEarned > 0 && (
            <div className="text-xs text-emerald-600 font-medium">
              Includes ${totalBonusEarned.toFixed(2)} bonus!
            </div>
          )}
        </div>
      </div>

      {/* Goal Section with Target Icon */}
      <div className="w-full mb-3">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <Target className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-gray-700">Goal: ${savingsGoal.toFixed(2)}</span>
        </div>

        {/* Goal Progress Details */}
        <div className="flex items-center justify-between w-full mb-2">
          <span className="text-xs text-gray-500">Progress</span>
          <span className="text-xs font-medium text-gray-700">{progressPercent.toFixed(0)}%</span>
        </div>

        <div className="w-full h-2 bg-gray-200 rounded-full mb-2 overflow-hidden">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${colorTheme.bg} transition-all duration-500 ease-out`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="text-xs text-gray-500 text-center">
          {totalSaved >= savingsGoal ? (
            <span className="text-emerald-600 font-medium">🎉 Goal achieved!</span>
          ) : (
            `$${(savingsGoal - totalSaved).toFixed(2)} to go`
          )}
        </div>
      </div>

      {/* Enhanced Action Buttons - Push to Bottom */}
      <div className="flex gap-2 w-full mt-auto">
        <button
          className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r ${colorTheme.bg} text-white hover:shadow-lg transform hover:scale-105 transition-all duration-200`}
          onClick={() => onChildPress(child)}
        >
          <div className="flex items-center justify-center space-x-1">
            <span>View</span>
          </div>
        </button>
        <button
          className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md transform hover:scale-105 transition-all duration-200"
          onClick={() => onAddMoney(child)}
        >
          <div className="flex items-center justify-center space-x-1">
            <span>Add $</span>
          </div>
        </button>
      </div>
    </div>
  );
};

// Updated Children Grid with staggered animations
const EnhancedChildrenGrid = ({ children, onChildPress, onAddMoney, onChildDeleted }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
      {children.map((child, index) => (
        <div
          key={child.id}
          className="animate-fadeInUp"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <EnhancedChildCard
            child={child}
            onChildPress={onChildPress}
            onAddMoney={onAddMoney}
            onChildDeleted={onChildDeleted}
          />
        </div>
      ))}
    </div>
  );
};

// Complete Family Dashboard Section Component
const FamilyDashboardSection = ({ children, onNavigate, onRefreshNeeded }) => {
  const [localChildren, setLocalChildren] = useState(children);

  // Sync with parent prop when it changes
  React.useEffect(() => {
    setLocalChildren(children);
  }, [children]);

  const handleChildPress = (child) => {
    onNavigate('childDashboard', child, { refreshParent: onRefreshNeeded });
  };

  const handleAddMoney = (child) => {
    onNavigate('addMoney', child);
  };

  const handleChildDeleted = (childId) => {
    setLocalChildren(prev => prev.filter(c => c.id !== childId));
  };

  return (
    <div className="px-4 sm:px-6 mb-6 sm:mb-8">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Family Members</h2>
          <p className="text-sm sm:text-base text-gray-600">Manage your children's savings accounts and goals</p>
        </div>
        <div className="flex-shrink-0">
          <button
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 sm:px-6 sm:py-2 rounded-xl font-medium transition-colors shadow-lg"
            onClick={() => onNavigate('addChild')}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="text-sm sm:text-base">Add Child</span>
          </button>
        </div>
      </div>

      {/* Children Grid or Empty State */}
      {localChildren.length === 0 ? (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-8 sm:p-12 text-center">
          <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🌱</div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
            No children added yet
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto leading-relaxed">
            Add your first child to start their savings journey and help them grow their financial future!
          </p>
          <button
            className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-medium transition-colors text-sm sm:text-base"
            onClick={() => onNavigate('addChild')}
          >
            Add Your First Child
          </button>
        </div>
      ) : (
        <EnhancedChildrenGrid
          children={localChildren}
          onChildPress={handleChildPress}
          onAddMoney={handleAddMoney}
          onChildDeleted={handleChildDeleted}
        />
      )}
    </div>
  );
};

export { EnhancedChildCard, EnhancedChildrenGrid, FamilyDashboardSection };